import React from 'react';
import './CacheSuggestions.css';

/**
 * CacheSuggestions Component
 * 
 * Displays similar cached reports as suggestions when exact match not found.
 * Users can select a cached report or proceed with new generation.
 */
function CacheSuggestions({ suggestions, onSelect, onGenerateNew, onClose }) {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return '#27ae60'; // Green
    if (similarity >= 0.85) return '#f39c12'; // Orange
    return '#e74c3c'; // Red
  };

  const getModelDisplayName = (modelId) => {
    if (!modelId) return null;
    
    if (modelId.includes('claude-3-7')) return 'Claude 3.7';
    if (modelId.includes('claude-3-5')) return 'Claude 3.5';
    if (modelId.includes('claude-3')) return 'Claude 3';
    if (modelId.includes('claude-4')) return 'Claude 4';
    if (modelId.includes('nova-pro')) return 'Nova Pro';
    if (modelId.includes('nova-lite')) return 'Nova Lite';
    if (modelId.includes('llama')) return 'Llama';
    if (modelId.includes('deepseek')) return 'DeepSeek';
    
    return modelId.split('.').pop().split('-')[0];
  };

  return (
    <div className="cache-suggestions-overlay">
      <div className="cache-suggestions-modal">
        <div className="cache-suggestions-header">
          <h3>🔍 Similar Reports Found</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="cache-suggestions-info">
          <p>We found {suggestions.length} similar report{suggestions.length > 1 ? 's' : ''} in your history.</p>
          <p className="cache-hint">Select one to view it instantly, or generate a new report.</p>
        </div>

        <div className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <div 
              key={index} 
              className="suggestion-card"
              onClick={() => onSelect(suggestion.report)}
            >
              <div className="suggestion-header">
                <div className="suggestion-title">
                  <span className="suggestion-number">#{index + 1}</span>
                  <h4>{suggestion.title}</h4>
                </div>
                <div 
                  className="similarity-badge"
                  style={{ backgroundColor: getSimilarityColor(suggestion.similarity) }}
                >
                  {Math.round(suggestion.similarity * 100)}% match
                </div>
              </div>

              <div className="suggestion-details">
                <div className="suggestion-info">
                  <span className="info-label">Backend:</span>
                  <span className="info-value">{suggestion.backend || 'traditional'}</span>
                </div>
                <div className="suggestion-info">
                  <span className="info-label">Generated:</span>
                  <span className="info-value">{formatDate(suggestion.generated_at)}</span>
                </div>
              </div>

              {/* NEW: Generation Settings */}
              {suggestion.generation_settings && (
                <div className="suggestion-settings">
                  {suggestion.generation_settings.model_id && (
                    <span className="setting-badge model-badge">
                      🤖 {getModelDisplayName(suggestion.generation_settings.model_id)}
                    </span>
                  )}
                  {suggestion.generation_settings.questionnaire_mode && (
                    <span className="setting-badge questionnaire-badge">
                      📋 8Q
                    </span>
                  )}
                  {suggestion.generation_settings.workflow_type && (
                    <span className="setting-badge workflow-badge">
                      🔗 {suggestion.generation_settings.workflow_type}
                    </span>
                  )}
                  {suggestion.generation_settings.advanced_mode && (
                    <span className="setting-badge advanced-badge-small">
                      🚀 Advanced
                    </span>
                  )}
                </div>
              )}

              <div className="suggestion-action">
                <span>Click to view this report →</span>
              </div>
            </div>
          ))}
        </div>

        <div className="cache-suggestions-actions">
          <button 
            className="btn-generate-new"
            onClick={onGenerateNew}
          >
            ✨ Generate New Report Instead
          </button>
          <button 
            className="btn-close-suggestions"
            onClick={onClose}
          >
            ← Back to Form
          </button>
        </div>
      </div>
    </div>
  );
}

export default CacheSuggestions;
